import React, { createContext, useState, useContext, ReactNode } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Define the options that the modal accepts.
export type ModalOptions = {
  title?: string;
  message?: string;
  preventClose?: boolean; // If true, users can't click away or press Escape to close
  displayAsPre?: boolean; // If true, display message in a <pre> block for better formatting
  onConfirm?: () => void;
  onCancel?: () => void;
};

// Define the context type.
type ModalContextType = {
  showModal: (options: ModalOptions) => void;
  hideModal: () => void;
};

// Create the context.
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Create the provider component.
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalOptions, setModalOptions] = useState<ModalOptions | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const showModal = (options: ModalOptions) => {
    setModalOptions(options);
    setIsVisible(true);
  };

  const handleConfirm = () => {
    if (modalOptions?.onConfirm) {
      modalOptions.onConfirm();
    }
    hideModal();
  };

  const handleCancel = () => {
    if (modalOptions?.onCancel) {
      modalOptions.onCancel();
    }
    hideModal();
  };

  const hideModal = () => {
    setIsVisible(false);
    setModalOptions(null);
  };

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}
      {/* Global Modal Container */}
      {isVisible && modalOptions && (
        <div className="modal-container">
          <Dialog
            keepMounted
            open={isVisible}
            // Custom onClose handler that checks for preventClose flag.
            onClose={hideModal}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {modalOptions.title || "Alert"}
            </DialogTitle>
            <DialogContent>
              { modalOptions.displayAsPre ? (
                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", backgroundColor: "#1f1f1f", padding: "1em", borderRadius: "4px" }}>
                  {modalOptions.message || "Are you sure?"}
              </pre>
              ) : (
                <DialogContentText id="alert-dialog-description">
                  {modalOptions.message || "Are you sure?"}
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleConfirm} autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </ModalContext.Provider>
  );
};

// Custom hook for consuming the modal context.
export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
